import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getTenantId } from "../../../redux/features/auth/authSlice";
import { useLazyGetAllTenantProductsQuery } from "../../../redux/services/productApiSlice";
import { useGetTrackNTraceAsnLinesQuery } from "../../../redux/services/trackntraceApiSlice";
import { useTenantSitesAndZones } from "../../../hooks/useTenantSitesAndZones";
import { removeEmptyKeys } from "../../../utils";
import { UICalendar } from "../../common/uiCalendar";
import moment from "moment";
import "./style.css";
import { exportRowsToWorkbook } from "./exportWorkbook";
import { Page } from "../../../routes/config";
import {
  extendTrail,
  makeTrailItem,
  writeReportTrailState,
} from "./reportTrail";
import { ReportToolbar } from "../../common/reportToolbar";
import Select from "../../common/selectInput/Select";
import Input from "../../common/input/Input";
import { UIAgGridColumn, UIAgGridTable } from "../../common/agGridTable";
import { Pagination } from "../../common/pagination";
import { MetricCard } from "./components/MetricCard";
import { Activity, BarcodeIcon, Layers, MapPin } from "lucide-react";
import { MetricSkeleton } from "./components/MetricSkeleton";

type ReportType = "transfer" | "receive";

type LineItemRow = {
  key: string;
  epc: string;
  upc: string;
  productDescription: string;
  tagId: string;
  transferredFromSite: string;
  transferredFromZone: string;
  transferredToSite: string;
  transferredToZone: string;
  dateTime: string;
};

export const AsnLineItemsDetails = ({
  reportType,
}: {
  reportType: ReportType;
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const asn = searchParams.get("asn") ?? "";
  const from = searchParams.get("from") ?? undefined;
  const to = searchParams.get("to") ?? undefined;
  const tenantIdFromQuery = searchParams.get("tenantId")?.trim() ?? "";
  const transferredFromSiteFromQuery =
    searchParams.get("transferredFromSite")?.trim() ?? "";
  const transferredFromZoneFromQuery =
    searchParams.get("transferredFromZone")?.trim() ?? "";
  const transferredToSiteFromQuery =
    searchParams.get("transferredToSite")?.trim() ?? "";
  const transferredToZoneFromQuery =
    searchParams.get("transferredToZone")?.trim() ?? "";
  const siteIdFromQuery = searchParams.get("siteId")?.trim() || undefined;
  const zoneIdFromQuery = searchParams.get("zoneId")?.trim() || undefined;
  const identityIdFromQuery =
    searchParams.get("identityId")?.trim() || undefined;

  const tenantId = useSelector(getTenantId);
  const resolvedTenantId = tenantId.trim() || tenantIdFromQuery;
  const {
    zoneRows,
    siteNameById,
    zoneNameById,
    siteOptionsById,
    zoneOptionsById,
  } = useTenantSitesAndZones(resolvedTenantId || undefined);

  const [epcFilter, setEpcFilter] = useState("");
  const [skuFilter, setSkuFilter] = useState("");
  const [tagIdFilter, setTagIdFilter] = useState("");
  const [siteId, setSiteId] = useState<string | undefined>(siteIdFromQuery);
  const [zoneId, setZoneId] = useState<string | undefined>(zoneIdFromQuery);
  const [dateRange, setDateRange] = useState<moment.Moment[]>([]);
  const [appliedEpcFilter, setAppliedEpcFilter] = useState("");
  const [appliedSkuFilter, setAppliedSkuFilter] = useState("");
  const [appliedTagIdFilter, setAppliedTagIdFilter] = useState("");
  const [appliedSiteId, setAppliedSiteId] = useState<string | undefined>(
    siteIdFromQuery,
  );
  const [appliedZoneId, setAppliedZoneId] = useState<string | undefined>(
    zoneIdFromQuery,
  );
  const [appliedDateRange, setAppliedDateRange] = useState<moment.Moment[]>([]);
  const [page, setPage] = useState(1);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // # Command Line: While sending requests to the backend, the default limit is set to 10,000.
  const [pageSize, setPageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState<{
    field?: keyof LineItemRow;
    order?: "ascend" | "descend";
  }>({});

  const queryArgs = useMemo(
    () =>
      removeEmptyKeys({
        asn: asn.trim(),
        tenantId: resolvedTenantId || undefined,
        reportType,
        from,
        to,
        identityId: identityIdFromQuery || undefined,
        // # Command Line: If the user changes the limit, the payload is also updated accordingly.
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
    [
      resolvedTenantId,
      reportType,
      asn,
      from,
      to,
      identityIdFromQuery,
      pageSize,
      page,
    ],
  );

  const { data, isFetching } = useGetTrackNTraceAsnLinesQuery(
    queryArgs as { asn: string; [key: string]: unknown },
    {
      skip: !resolvedTenantId || !asn.trim(),
    },
  );

  const response = useMemo(() => (data as any)?.result ?? data, [data]);
  // # Command Line: The response we are receiving includes: Count, Total Enablements, Total De-Enablements.
  const lineItems = useMemo(() => {
    // Support all known backend response shapes for ASN line drill-down.
    const rows = response?.lines ?? response?.records ?? response?.rows ?? [];
    return Array.isArray(rows) ? rows : [];
  }, [response]);
  const [triggerGetProducts] = useLazyGetAllTenantProductsQuery();
  const [productDescriptionByUpc, setProductDescriptionByUpc] = useState<
    Record<string, string>
  >({});

  const uniqueUpcs = useMemo(() => {
    const upcs = lineItems
      .map((item: any) =>
        String(
          item?.upc ?? item?.sku ?? item?.productUPC ?? item?.UPC ?? "",
        ).trim(),
      )
      .filter(Boolean);
    return Array.from(new Set(upcs));
  }, [lineItems]);

  useEffect(() => {
    let mounted = true;

    const fetchProductDescriptions = async () => {
      if (!resolvedTenantId || uniqueUpcs.length === 0) {
        if (mounted) setProductDescriptionByUpc({});
        return;
      }

      const entries = await Promise.all(
        uniqueUpcs.map(async (upc) => {
          try {
            const response: any = await triggerGetProducts(
              { tenantId: resolvedTenantId, upc },
              true,
            );
            const payload = response?.data?.result ?? response?.data ?? {};
            const products = Array.isArray(payload?.records)
              ? payload.records
              : Array.isArray(payload?.rows)
                ? payload.rows
                : Array.isArray(payload)
                  ? payload
                  : [];

            const matchingProduct =
              products.find(
                (p: any) =>
                  String(p?.upc ?? p?.UPC ?? p?.sku ?? p?.skuUpc ?? "")
                    .trim()
                    .toLowerCase() === upc.toLowerCase(),
              ) ?? products[0];

            const description = String(
              matchingProduct?.description ??
                matchingProduct?.productDescription ??
                matchingProduct?.name ??
                "",
            ).trim();

            return [upc, description] as const;
          } catch {
            return [upc, ""] as const;
          }
        }),
      );

      if (!mounted) return;
      setProductDescriptionByUpc(Object.fromEntries(entries));
    };

    fetchProductDescriptions();
    return () => {
      mounted = false;
    };
  }, [resolvedTenantId, uniqueUpcs, triggerGetProducts]);

  const tableData = useMemo<LineItemRow[]>(() => {
    const isUuid = (value: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value.trim(),
      );
    const resolveName = (raw: unknown, map: Map<string, string>) => {
      const value = String(raw ?? "").trim();
      if (!value) return "";
      if (map.has(value)) return map.get(value) ?? "";
      return isUuid(value) ? "" : value;
    };

    return lineItems.map((item: any, index: number) => ({
      key: String(
        item?._id ?? item?.id ?? item?.epc ?? item?.tagId ?? `${asn}-${index}`,
      ),
      epc: String(item?.epc ?? item?.EPC ?? ""),
      upc: String(
        item?.upc ?? item?.sku ?? item?.productUPC ?? item?.UPC ?? "",
      ),
      productDescription: String(
        item?.description ??
          item?.productDescription ??
          productDescriptionByUpc[
            String(
              item?.upc ?? item?.sku ?? item?.productUPC ?? item?.UPC ?? "",
            ).trim()
          ] ??
          "",
      ),
      tagId: String(item?.tagId ?? item?.tagID ?? item?.primaryId ?? ""),
      transferredFromSite:
        resolveName(item?.transferredFromSite, siteNameById) ||
        resolveName(item?.transferredFromLocation, siteNameById) ||
        resolveName(item?.siteId, siteNameById) ||
        resolveName(item?.site, siteNameById) ||
        transferredFromSiteFromQuery ||
        "",
      transferredFromZone:
        resolveName(item?.transferredFromZone, zoneNameById) ||
        resolveName(item?.zoneId, zoneNameById) ||
        resolveName(item?.zone, zoneNameById) ||
        transferredFromZoneFromQuery ||
        "",
      transferredToSite:
        resolveName(item?.transferredToSite, siteNameById) ||
        resolveName(item?.transferredToLocation, siteNameById) ||
        resolveName(item?.destinationSiteId, siteNameById) ||
        transferredToSiteFromQuery ||
        "",
      transferredToZone:
        resolveName(item?.transferredToZone, zoneNameById) ||
        resolveName(item?.destinationZoneId, zoneNameById) ||
        resolveName(item?.destinationZone, zoneNameById) ||
        transferredToZoneFromQuery ||
        "",
      dateTime: String(
        item?.lastUpdatedAt ??
          item?.updatedAt ??
          item?.operationTime ??
          item?.createdAt ??
          "",
      ),
    }));
  }, [
    lineItems,
    asn,
    productDescriptionByUpc,
    siteNameById,
    zoneNameById,
    transferredFromSiteFromQuery,
    transferredFromZoneFromQuery,
    transferredToSiteFromQuery,
    transferredToZoneFromQuery,
  ]);

  const ALL_COLUMNS = useMemo<UIAgGridColumn<LineItemRow>[]>(() => {
    const base: UIAgGridColumn<LineItemRow>[] = [
      {
        key: "tagId",
        title: "Tag ID",
        dataIndex: "tagId",
        minWidth: 160,
        flex: 1,
        sorter: true,
        render: (value: unknown) => (value ? String(value) : "-"),
      },
      {
        key: "epc",
        title: "EPC",
        dataIndex: "epc",
        minWidth: 220,
        flex: 1,
        sorter: true,
        render: (value: unknown) => (value ? String(value) : "-"),
      },
      {
        key: "upc",
        title: "UPC/SKU",
        dataIndex: "upc",
        minWidth: 160,
        flex: 1,
        sorter: true,
        render: (value: unknown) => (value ? String(value) : "-"),
      },
      {
        key: "productDescription",
        title: "Product Description",
        dataIndex: "productDescription",
        minWidth: 280,
        flex: 2,
        sorter: true,
        render: (value: unknown) => (value ? String(value) : "-"),
      },
    ];

    if (reportType === "transfer") {
      base.push(
        {
          key: "transferredFromSite",
          title: "Transferred From Site",
          dataIndex: "transferredFromSite",
          minWidth: 180,
          flex: 1,
          sorter: true,
          render: (value: unknown) => (value ? String(value) : "-"),
        },
        {
          key: "transferredFromZone",
          title: "Transferred From Zone",
          dataIndex: "transferredFromZone",
          minWidth: 180,
          flex: 1,
          sorter: true,
          render: (value: unknown) => (value ? String(value) : "-"),
        },
        {
          key: "transferredToSite",
          title: "Transferred To Site",
          dataIndex: "transferredToSite",
          minWidth: 180,
          flex: 1,
          sorter: true,
          render: (value: unknown) => (value ? String(value) : "-"),
        },
        {
          key: "transferredToZone",
          title: "Transferred To Zone",
          dataIndex: "transferredToZone",
          minWidth: 180,
          flex: 1,
          sorter: true,
          render: (value: unknown) => (value ? String(value) : "-"),
        },
      );
    }

    return base;
  }, [reportType]);
  const [columns, setColumns] = useState(ALL_COLUMNS);

  const filteredRows = useMemo(() => {
    const epcQ = appliedEpcFilter.trim().toLowerCase();
    const skuQ = appliedSkuFilter.trim().toLowerCase();
    const tagQ = appliedTagIdFilter.trim().toLowerCase();
    const siteQ = (appliedSiteId ? (siteNameById.get(appliedSiteId) ?? "") : "")
      .trim()
      .toLowerCase();
    const zoneQ = (appliedZoneId ? (zoneNameById.get(appliedZoneId) ?? "") : "")
      .trim()
      .toLowerCase();
    const hasDate = appliedDateRange.length === 2;
    const fromMs = hasDate
      ? moment.utc(appliedDateRange[0]).startOf("day").valueOf()
      : 0;
    const toMs = hasDate
      ? moment.utc(appliedDateRange[1]).endOf("day").valueOf()
      : 0;

    return tableData.filter((row) => {
      const epcMatch = !epcQ || row.epc.toLowerCase().includes(epcQ);
      const skuMatch = !skuQ || row.upc.toLowerCase().includes(skuQ);
      const tagMatch = !tagQ || row.tagId.toLowerCase().includes(tagQ);
      const siteHaystack =
        `${row.transferredFromSite} ${row.transferredToSite}`.toLowerCase();
      const zoneHaystack =
        `${row.transferredFromZone} ${row.transferredToZone}`.toLowerCase();
      const siteMatch = !siteQ || siteHaystack.includes(siteQ);
      const zoneMatch = !zoneQ || zoneHaystack.includes(zoneQ);
      const rowTime = row.dateTime ? moment(row.dateTime).valueOf() : 0;
      const dateMatch = !hasDate || (rowTime >= fromMs && rowTime <= toMs);
      return (
        epcMatch && skuMatch && tagMatch && siteMatch && zoneMatch && dateMatch
      );
    });
  }, [
    tableData,
    appliedEpcFilter,
    appliedSkuFilter,
    appliedTagIdFilter,
    appliedSiteId,
    appliedZoneId,
    appliedDateRange,
    siteNameById,
    zoneNameById,
  ]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.field || !sortConfig.order) return filteredRows;
    const dir = sortConfig.order === "ascend" ? 1 : -1;
    const field = sortConfig.field;
    return [...filteredRows].sort((a, b) => {
      if (field === "dateTime")
        return (
          (moment(a.dateTime).valueOf() - moment(b.dateTime).valueOf()) * dir
        );
      return String(a[field] ?? "").localeCompare(String(b[field] ?? "")) * dir;
    });
  }, [filteredRows, sortConfig]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<LineItemRow> | SorterResult<LineItemRow>[],
  ) => {
    setPage(newPagination.current ?? 1);
    setPageSize(newPagination.pageSize ?? 50);
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    setSortConfig({
      field: (s?.field as keyof LineItemRow | undefined) ?? undefined,
      order: (s?.order as "ascend" | "descend" | undefined) ?? undefined,
    });
  };

  const applyFilters = () => {
    setAppliedEpcFilter(epcFilter);
    setAppliedSkuFilter(skuFilter);
    setAppliedTagIdFilter(tagIdFilter);
    setAppliedSiteId(siteId);
    setAppliedZoneId(zoneId);
    setAppliedDateRange(dateRange);
    setPage(1);
  };

  const resetFilters = () => {
    setEpcFilter("");
    setSkuFilter("");
    setTagIdFilter("");
    setSiteId(undefined);
    setZoneId(undefined);
    setDateRange([]);
    setAppliedEpcFilter("");
    setAppliedSkuFilter("");
    setAppliedTagIdFilter("");
    setAppliedSiteId(undefined);
    setAppliedZoneId(undefined);
    setAppliedDateRange([]);
    setPage(1);
    setPageSize(50);
  };

  const exportExcel = () => {
    const header = columns.map((c) => String(c.title ?? ""));
    const excelRows: Array<Array<string | number>> = [
      header,
      ...sortedRows.map((row) => [
        row.tagId,
        row.epc,
        row.upc,
        row.productDescription,
        ...(reportType === "transfer"
          ? [
              row.transferredFromSite,
              row.transferredFromZone,
              row.transferredToSite,
              row.transferredToZone,
            ]
          : []),
      ]),
    ];
    exportRowsToWorkbook(
      `asn-line-items-${asn}.xlsx`,
      "ASN Line Items",
      excelRows,
    );
  };

  if (!asn.trim()) {
    return (
      <div className="di-reports-page">
        <p className="details-missing-user">
          No ASN selected. Open this page from the {reportType} report.
        </p>
      </div>
    );
  }

  return (
    <div className="shadow-card h-full flex flex-col min-h-0 rounded-md gap-0">
  <div className="grid grid-cols-1 md:grid-cols-4 gap-5 items-stretch">
  {/* Left Section: Active ASN Pipeline Route Path */}
  <div className="md:col-span-3 bg-gradient-to-r from-white via-slate-50/40 to-white border border-slate-200/80 rounded-2xl shadow-xs p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-6 relative overflow-hidden">
    
    {/* Micro decorative top accent line */}
    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-teal-500/0 via-teal-500/30 to-teal-500/0" />

    <div className="flex items-start sm:items-center gap-4 min-w-0 flex-1">
      {/* Route Direction Geometric Icon */}
      <div className="w-10 h-10 bg-gradient-to-br from-teal-50 to-teal-100/60 border border-teal-200/60 rounded-xl flex items-center justify-center flex-shrink-0 shadow-inner text-[var(--primary)] text-sm font-black shrink-0">
        ➔
      </div>

      <div className="space-y-2 min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block font-sans">
            Active ASN Pipeline
          </span>
          <span className="inline-flex items-center bg-amber-50 text-amber-700 border border-amber-200/60 text-[9px] font-extrabold px-2 py-0.5 rounded-full animate-pulse uppercase tracking-wider">
            En-Route
          </span>
        </div>

        {/* Multi-Hub Transfer String Track */}
        <div className="text-sm font-semibold text-slate-800 flex flex-wrap items-center gap-2">
          <span className="font-extrabold text-slate-900">
            Vijayawada Hub
          </span>
          <span className="inline-flex text-[10px] text-teal-700 bg-teal-50 border border-teal-200/40 px-2 py-0.5 rounded font-mono font-bold">
            Gates 1-4 Outbound
          </span>

          <svg
            className="w-4 h-4 text-slate-400 mx-0.5 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7" />
          </svg>

          <span className="font-extrabold text-indigo-950">
            Bengaluru Hub
          </span>
          <span className="inline-flex text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200/40 px-2 py-0.5 rounded font-mono font-bold">
            Zones A-B Intake
          </span>
        </div>
      </div>
    </div>
  </div>

  {/* Right Section: Separate Total Line Items Metric Card */}
  <div className="bg-gradient-to-br from-white via-white to-[var(--primaryExtraLight)] border border-l-4 border-l-[var(--primary)] border-y-slate-200 border-r-slate-200 rounded-2xl p-4 flex items-center justify-between shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm relative overflow-hidden group">
    {/* Micro design backdrop element glow */}
    <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-[rgb(var(--primaryRgb)/0.07)] rounded-full blur-lg group-hover:scale-125 transition-transform duration-300" />
    
    <div className="space-y-1.5 min-w-0 z-10">
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block font-sans antialiased">
        Total Line Items
      </span>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-black font-mono tracking-tight text-slate-900">
          500
        </span>
        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider ml-0.5">
          Units
        </span>
      </div>
    </div>

    {/* Metric Icon Container Box */}
    <div className="p-2.5 rounded-xl border border-[rgb(var(--primaryRgb)/0.2)] bg-[var(--primaryLight)] text-[var(--primary)] flex items-center justify-center shrink-0 transition-all group-hover:bg-[var(--primaryLight)] group-hover:border-[rgb(var(--primaryRgb)/0.2)] duration-200 shadow-2xs">
      <svg
        className="w-5 h-5 stroke-current fill-none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
  </div>
</div>

      <div className="mt-3">
        <ReportToolbar
          searchValue={epcFilter}
          onSearchChange={setEpcFilter}
          onSearchEnter={applyFilters}
          searchPlaceholder="Search EPC"
          showAdvancedFilters={showAdvancedFilters}
          onToggleAdvancedFilters={() =>
            setShowAdvancedFilters((show) => !show)
          }
          onApplyFilters={applyFilters}
          onResetFilters={resetFilters}
          onExportExcel={exportExcel}
        >
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 ${
              reportType === "transfer" ? "lg:grid-cols-4" : "lg:grid-cols-4"
            } gap-5`}
          >
            {/* EPC */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                EPC
              </label>
              <Input
                placeholder="Please enter EPC"
                value={epcFilter}
                onChange={(e) => setEpcFilter(e.target.value)}
                allowClear
              />
            </div>

            {/* UPC/SKU */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                UPC/SKU
              </label>
              <Input
                placeholder="Please enter UPC/SKU"
                value={skuFilter}
                onChange={(e) => setSkuFilter(e.target.value)}
                allowClear
              />
            </div>

            {/* Tag ID */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Tag ID
              </label>
              <Input
                placeholder="Please enter Tag ID"
                value={tagIdFilter}
                onChange={(e) => setTagIdFilter(e.target.value)}
                allowClear
              />
            </div>

            {/* Transfer-specific Site */}
            {reportType === "transfer" && (
              <div className="w-full">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Site
                </label>
                <Select
                  placeholder="Select Site"
                  value={siteId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSiteId(value);
                    setZoneId("");
                  }}
                  onClear={() => {
                    setSiteId("");
                    setZoneId("");
                  }}
                  allowClear
                  options={siteOptionsById}
                />
              </div>
            )}

            {/* Transfer-specific Zone */}
            {reportType === "transfer" && (
              <div className="w-full">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Zone
                </label>
                <Select
                  placeholder="Select Zone"
                  value={zoneId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setZoneId(value);

                    const z = zoneRows.find(
                      (r) => String(r.id) === String(value),
                    );

                    if (z?.siteId) {
                      setSiteId(String(z.siteId));
                    }
                  }}
                  onClear={() => setZoneId("")}
                  allowClear
                  options={zoneOptionsById}
                />
              </div>
            )}

            {/* Time Range */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Time Range
              </label>
              <UICalendar
                params={dateRange}
                setParams={setDateRange}
                handleChange={(dates: moment.Moment[]) =>
                  setDateRange(dates || [])
                }
                className="w-full"
              />
            </div>
          </div>
        </ReportToolbar>
      </div>

      <div className="table-container">
        <UIAgGridTable
          columns={columns}
          data={pagedRows}
          loading={isFetching}
          rowHeight={44}
          // onSortChange={handleGridSortChange}
          onRowClick={
            reportType === "receive"
              ? (record: LineItemRow) => {
                  const params = new URLSearchParams();

                  const resolvedTag = record.tagId || record.epc;
                  const resolvedDi = record.tagId || record.epc;

                  if (!resolvedDi) return;

                  if (resolvedTag) {
                    params.set("tagId", resolvedTag);
                  }

                  params.set("diId", resolvedDi);

                  if (record.epc) {
                    params.set("epc", record.epc);
                  }

                  if (record.upc) {
                    params.set("sku", record.upc);
                  }

                  if (record.transferredToSite) {
                    params.set("siteName", record.transferredToSite);
                  }

                  if (record.transferredToZone) {
                    params.set("zoneName", record.transferredToZone);
                  }

                  if (record.productDescription) {
                    params.set("description", record.productDescription);
                  }

                  const to = `${Page.REPORTS_RECEIVE_TAG_EPCIS}?${params.toString()}`;

                  const nextLabel = resolvedTag
                    ? `Tag EPCIS (${resolvedTag})`
                    : "Tag EPCIS History";

                  const nextItem = makeTrailItem(nextLabel, to);
                  const nextTrail = extendTrail(location, nextItem);

                  navigate(to, {
                    state: writeReportTrailState(location.state, nextTrail),
                  });
                }
              : undefined
          }
        />
        <Pagination
          current={page}
          pageSize={pageSize}
          className="rounded-b-md border-t border-slate-200 dark:border-gray-700"
          total={sortedRows.length}
          hideOnSinglePage={false}
          showSizeChanger={true}
          onPageChange={(p) => {
            setPage(p);
          }}
          onPageSizeChange={(newSize) => {
            setPage(1);
            setPageSize(newSize);
          }}
        />
      </div>
    </div>
  );
};
